// Funnel analytics — typed PostHog wrapper.
//
// Why this exists:
// • PostHog autocapture already gives us pageviews + raw clicks, but the
//   product team needs to see CONVERSION RATES at each named step. Raw
//   click events are noisy and can't be turned into a funnel without
//   stable event names.
// • This module defines the canonical event vocabulary in one place so
//   we never end up with "signupClicked", "signup-clicked" and
//   "signup_clicked" all referring to the same step.
// • Every event is mirrored to Microsoft Clarity (loaded only on
//   production via index.html) so we can filter session recordings by
//   funnel step.
//
// Adding a new step: extend `FunnelEvent` union + bump the SCHEMA_VERSION
// so PostHog can scope dashboards to the right version when the funnel
// changes.

import { clarityEvent, claritySetTag, clarityIdentify } from './clarity';

export const ANALYTICS_SCHEMA_VERSION = 1;

export type FunnelEvent =
  // Landing
  | 'landing_cta_clicked'           // any "Start Reshaping / Get Started" tap
  | 'landing_login_clicked'         // "Log In" header / hero tap
  // Auth modal
  | 'signup_modal_opened'
  | 'signup_submitted'              // user pressed Create Account
  | 'signup_succeeded'              // backend returned 200 + user is in
  | 'signup_failed'
  | 'login_submitted'
  | 'login_succeeded'
  | 'login_failed'
  | 'google_oauth_started'
  // Onboarding
  | 'onboarding_started'
  | 'onboarding_step_completed'     // payload: { step: string }
  | 'onboarding_completed'
  | 'onboarding_abandoned'
  // Pricing / paywall
  | 'pricing_viewed'
  | 'pricing_plan_selected'         // payload: { plan: 'monthly' | 'yearly' | 'free' }
  | 'checkout_started'              // Stripe session opened
  | 'checkout_succeeded'
  | 'checkout_failed';

type Props = Record<string, string | number | boolean | undefined | null>;

declare global {
  interface Window {
    posthog?: {
      capture: (event: string, props?: Record<string, unknown>) => void;
      identify: (id: string, props?: Record<string, unknown>) => void;
      reset?: () => void;
    };
  }
}

/**
 * Fire a funnel event. Silently no-ops if PostHog hasn't loaded yet
 * (e.g., ad-blockers, slow networks) so we never break the user flow.
 * Mirrors the event to Microsoft Clarity (production-only) so session
 * recordings can be filtered by funnel step.
 */
export function track(event: FunnelEvent, props?: Props): void {
  try {
    if (typeof window !== 'undefined' && window.posthog) {
      window.posthog.capture(event, {
        ...(props || {}),
        schema_version: ANALYTICS_SCHEMA_VERSION,
      });
    }
  } catch {
    /* analytics must never crash the app */
  }
  // Clarity: send event name + propagate primitive props as session tags
  // so they're filterable in the Clarity dashboard.
  clarityEvent(event);
  if (props) {
    for (const key of Object.keys(props)) {
      const v = props[key];
      if (v == null) continue;
      claritySetTag(key, v);
    }
  }
}

/**
 * Tie all future events to a known user. Call once on
 * signup_succeeded / login_succeeded. Uses `email` as a stable secondary
 * key so we can cross-match in PostHog person merge.
 */
export function identifyUser(userId: string, traits?: Props): void {
  try {
    if (typeof window !== 'undefined' && window.posthog) {
      window.posthog.identify(userId, traits as Record<string, unknown>);
    }
  } catch {
    /* noop */
  }
  // Mirror to Clarity. Only forward primitive traits.
  const safeTraits: Record<string, string | number | boolean> = {};
  if (traits) {
    for (const k of Object.keys(traits)) {
      const v = traits[k];
      if (v != null) safeTraits[k] = v;
    }
  }
  clarityIdentify(userId, safeTraits);
}

/** Forget the current identity (use on logout). */
export function resetAnalytics(): void {
  try {
    window.posthog?.reset?.();
  } catch {
    /* noop */
  }
}
